import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowUpRight, Trophy, Users, Calendar, Sparkles,
  MessageCircle, MapPin, Phone, Target, Activity, CircleDot, Crown,
  Swords, Dumbbell, Shield, Zap, TrendingUp, Goal, Volleyball, Waves, Heart,
} from "lucide-react";
import { api, WHATSAPP_LINK, CLUB_LOCATION, CLUB_PRESIDENT, PHONE_DISPLAY } from "../lib/api";

const ICONS = {
  Target, Activity, CircleDot, Crown, Swords, Dumbbell, Shield, Zap,
  TrendingUp, Goal, Volleyball, Waves, Heart, Sparkles,
};

export default function Home() {
  const [branches, setBranches] = useState([]);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="absolute -top-32 -right-32 h-[520px] w-[520px] rounded-full bg-primary/15 blur-3xl" />
        <div className="absolute -bottom-32 -left-24 h-[420px] w-[420px] rounded-full bg-primary/10 blur-3xl" />
        {/* radial spotlight behind logo on mobile */}
        <div className="lg:hidden absolute inset-x-0 top-0 h-[70vh] bg-[radial-gradient(circle_at_50%_30%,hsl(202_100%_50%/0.25),transparent_60%)] pointer-events-none" />

        <div className="container-x relative pt-8 pb-14 md:pt-20 md:pb-28 grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">

          {/* MOBILE-ONLY HERO (app-like) */}
          <div className="lg:hidden col-span-full flex flex-col items-center text-center fade-in-up">
            <div className="relative mb-5 mt-2">
              <div className="absolute -inset-8 bg-primary/35 blur-3xl rounded-full animate-pulse" />
              <div className="absolute -inset-4 bg-primary/20 blur-2xl rounded-full" />
              <img
              src="/assets/fcs-logo.png"
              alt="FCS Focus Spor Kulübü"
              className="relative h-52 w-52 sm:h-64 sm:w-64 object-contain drop-shadow-[0_20px_60px_hsl(202_100%_50%/0.7)]"
              data-testid="hero-logo-mobile"
              />
            </div>

            <div className="eyebrow mb-2.5 justify-center">
              <span className="h-1.5 w-1.5 rounded-full bg-primary pulse-soft" />
              Tuzla · İstanbul · 2025
            </div>

            <h1 className="font-brand uppercase leading-[0.9]"
                style={{ fontSize: "clamp(2.75rem, 13vw, 4.25rem)" }}>
              Focus<br />Spor Kulübü
            </h1>
            <div className="mt-4 font-brand uppercase text-primary leading-none"
                 style={{ fontSize: "clamp(1.75rem, 8vw, 2.5rem)", letterSpacing: "0.12em" }}>
              Hedefe Odaklan
            </div>

            <p className="mt-5 px-2 text-sm leading-relaxed text-muted-foreground max-w-md">
              Çok branşlı kulüp yapımızla çocuklar, gençler ve yetişkinler için{" "}
              <span className="text-foreground font-semibold">eğitim, gelişim ve sportif başarı</span>.
            </p>

            <div className="mt-6 w-full max-w-sm grid grid-cols-1 gap-2.5">
              <Link to="/branslar" className="btn-primary w-full py-4" data-testid="hero-m-cta-branches">
                Branşları İncele <ArrowRight className="h-4 w-4" />
              </Link>
              <div className="grid grid-cols-2 gap-2.5">
                <Link to="/etkinlikler" className="btn-secondary w-full py-3" data-testid="hero-m-cta-events">
                  Etkinlik Kayıt
                </Link>
                <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-[#25D366] px-4 py-3 text-white font-display font-bold text-sm uppercase tracking-wider hover:scale-[1.02] active:scale-[0.98] transition"
                  data-testid="hero-m-cta-whatsapp"
                >
                  WhatsApp
                </a>
              </div>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-4 w-full max-w-md border-t border-border pt-6">
              <Stat number="20+" label="Spor Branşı" />
              <Stat number="2025" label="Kuruluş" />
              <Stat number="6+" label="Aktif Kurs" />
            </div>
          </div>

          {/* DESKTOP HERO */}
          <div className="hidden lg:block lg:col-span-7 fade-in-up">
            <div className="eyebrow mb-5">
              <span className="h-1.5 w-1.5 rounded-full bg-primary pulse-soft" />
              FCS · Tuzla — İstanbul · 2025
            </div>
            <h1 className="font-brand uppercase leading-[0.92]"
                style={{ fontSize: "clamp(3.25rem, 7vw, 6rem)" }}>
              Focus<br />Spor Kulübü
            </h1>
            <div className="mt-4 font-brand uppercase text-primary"
                 style={{ fontSize: "clamp(2.1rem, 3.6vw, 3.25rem)", letterSpacing: "0.14em", lineHeight: 0.95 }}>
              Hedefe Odaklan
            </div>
            <p className="mt-6 max-w-xl text-base md:text-lg text-muted-foreground leading-relaxed">
              Tuzla – İstanbul merkezli Focus Spor Kulübü; farklı spor branşlarında çocuklar, gençler ve
              yetişkinler için <span className="text-foreground font-semibold">eğitim, gelişim, organizasyon
              ve sportif başarı</span> odaklı bir kulüp yapısı sunar.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/branslar" className="btn-primary" data-testid="hero-cta-branches">
                Branşları İncele <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/etkinlikler" className="btn-secondary" data-testid="hero-cta-events">
                Etkinliğe Kayıt Ol
              </Link>
              <Link to="/iletisim" className="btn-ghost" data-testid="hero-cta-contact">
                Bizimle İletişime Geç →
              </Link>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-5 max-w-lg">
              <Stat number="20+" label="Spor Branşı" />
              <Stat number="2025" label="Kuruluş Yılı" />
              <Stat number="6+" label="Aktif Kurs" />
            </div>
          </div>

          <div className="hidden lg:block lg:col-span-5 relative">
            <div className="relative mx-auto max-w-md">
              <div className="absolute -inset-4 bg-primary/20 blur-3xl rounded-full" />
              <img
                src="/assets/fcs-logo.png"
                alt="FCS Focus Spor Kulübü logo"
                className="relative w-full drop-shadow-[0_20px_60px_hsl(202_100%_50%/0.55)]"
                data-testid="hero-logo"
              />
            </div>
            <div className="absolute -bottom-2 right-0 hidden md:flex items-center gap-2 rounded-full border border-border bg-card/80 backdrop-blur px-4 py-2">
              <span className="h-2 w-2 rounded-full bg-emerald-400" />
              <span className="font-display text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Kayıtlar Açıldı</span>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED BRANCHES */}
      <section className="section-pad" data-testid="branches-preview">
        <div className="container-x">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
            <div>
              <div className="eyebrow mb-3">Öne Çıkan Branşlar</div>
              <h2 className="heading-lg">20'ye Yakın Branş, <span className="text-primary">Tek Kulüp</span></h2>
            </div>
            <Link to="/branslar" className="btn-ghost" data-testid="branches-view-all">
              Tüm Branşlar <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {branches.slice(0, 8).map((b) => {
              const Icon = ICONS[b.icon] || Sparkles;
              return (
                <Link
                  key={b.slug}
                  to={`/branslar/${b.slug}`}
                  className="card-elev p-5 group block"
                  data-testid={`branch-card-${b.slug}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="h-11 w-11 rounded-md bg-primary/10 ring-1 ring-primary/20 flex items-center justify-center text-primary">
                      <Icon className="h-5 w-5" />
                    </div>
                    {b.is_active ? (
                      <span className="badge-status-active"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />Aktif</span>
                    ) : (
                      <span className="badge-status-soon"><span className="h-1.5 w-1.5 rounded-full bg-primary" />Yakında</span>
                    )}
                  </div>
                  <h3 className="font-display text-xl font-bold uppercase tracking-tight mb-1.5 group-hover:text-primary transition-colors">{b.name}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">{b.short_description}</p>
                  <div className="mt-4 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                    Detay <ArrowRight className="h-3 w-3" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* COMING SOON STRIPE */}
      <section className="border-y border-border bg-[hsl(218_55%_5%)]">
        <div className="container-x py-10 grid md:grid-cols-2 gap-6 items-center">
          <div>
            <div className="eyebrow mb-2">Yakında Aktif</div>
            <h3 className="heading-md">Yeni branşlar üzerinde çalışıyoruz</h3>
            <p className="mt-3 text-sm md:text-base text-muted-foreground max-w-lg leading-relaxed">
              Eğer bu branşların antrenörü, uzmanı ya da eğitmeni olduğunu düşünüyorsan bizimle iletişime geç.
              Branşları birlikte en kısa sürede aktif hale getirelim.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 justify-start md:justify-end">
            {branches.slice(8, 20).map((b) => (
              <span key={b.slug} className="rounded-full border border-border bg-card px-3 py-1.5 text-xs font-display font-bold uppercase tracking-wider hover:border-primary hover:text-primary transition">
                {b.name}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* COURSES TEASER */}
      <section className="section-pad" data-testid="courses-preview">
        <div className="container-x grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-3">Spor Kursları</div>
            <h2 className="heading-lg">Her yaşa, <span className="text-primary">her seviyeye</span> uygun program</h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              Mini sporcudan profesyonele uzanan yelpazemizde; çocuk hazırlık programlarından yetişkin
              kondisyon kurslarına kadar geniş bir kurs ailesi sunuyoruz.
            </p>
            <Link to="/kurslar" className="btn-primary mt-6" data-testid="courses-view-all">
              Kursları Gör <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            <Feature icon={Users} title="Disiplinli Eğitim" text="Yaş ve seviyeye göre özel hazırlanmış sınıflar." />
            <Feature icon={Calendar} title="Esnek Programlar" text="Hafta içi ve hafta sonu seçenekleri." />
            <Feature icon={Trophy} title="Hedef Odaklı" text="Sportif başarı ve gelişim odaklı koçluk." />
            <Feature icon={Sparkles} title="Modern Tesisler" text="Tuzla'da güvenli ve kaliteli antrenman ortamı." />
          </div>
        </div>
      </section>

      {/* EVENTS */}
      <section className="section-pad border-t border-border bg-[hsl(218_55%_5%)]" data-testid="events-preview">
        <div className="container-x">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
            <div>
              <div className="eyebrow mb-3">Etkinlikler</div>
              <h2 className="heading-lg">Yaklaşan <span className="text-primary">Organizasyonlar</span></h2>
            </div>
            <Link to="/etkinlikler" className="btn-ghost" data-testid="events-view-all">
              Tüm Etkinlikler <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid lg:grid-cols-3 gap-5">
            {events.slice(0, 3).map((e) => (
              <Link
                key={e.id}
                to={`/etkinlikler/${e.id}`}
                className="card-elev p-6 block group"
                data-testid={`event-card-${e.id}`}
              >
                <div className="flex items-center gap-2 mb-4">
                  <span className="badge-status-soon">{e.event_type}</span>
                  <span className="text-xs text-muted-foreground font-display uppercase tracking-wider">{e.branch}</span>
                </div>
                <h3 className="font-display text-2xl font-bold uppercase tracking-tight group-hover:text-primary transition-colors">{e.name}</h3>
                <div className="mt-4 space-y-2 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2"><Calendar className="h-4 w-4 text-primary" />{e.date} · {e.time}</div>
                  <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" />{e.location}</div>
                  <div className="flex items-center gap-2"><Users className="h-4 w-4 text-primary" />Yaş: {e.age_category} · Kontenjan: {e.capacity}</div>
                </div>
                <div className="mt-5 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-primary">
                  Etkinlik Detayı <ArrowRight className="h-3 w-3" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PRESIDENT MESSAGE */}
      <section className="section-pad" data-testid="president-message">
        <div className="container-x grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="relative aspect-square max-w-sm mx-auto rounded-2xl border border-border bg-gradient-to-br from-primary/15 to-transparent overflow-hidden grid-bg">
              <div className="absolute inset-0 flex items-center justify-center p-10">
                <img src="/assets/fcs-logo.png" alt="FCS" className="w-3/4 opacity-90 drop-shadow-[0_10px_40px_hsl(202_100%_50%/0.5)]" />
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="eyebrow mb-3">Kulüp Başkanı'ndan</div>
            <h2 className="heading-lg">"Disiplinin ve <span className="text-primary">odaklanmanın</span> kulübü."</h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              Focus Spor Kulübü olarak hedefimiz; çocuklarımıza, gençlerimize ve yetişkinlerimize güvenli,
              disiplinli ve kaliteli bir spor ortamı sunmak. Tuzla'dan başlayarak farklı branşlarda güçlü
              sporcular, bilinçli aileler ve sürdürülebilir bir kulüp kültürü oluşturuyoruz.
            </p>
            <div className="mt-6 flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-primary/15 ring-1 ring-primary/30 flex items-center justify-center">
                <span className="font-display font-black text-primary">VC</span>
              </div>
              <div>
                <div className="font-display font-bold uppercase tracking-wider">{CLUB_PRESIDENT}</div>
                <div className="text-xs text-muted-foreground">Kulüp Başkanı · {CLUB_LOCATION}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT BANNER */}
      <section className="border-t border-border bg-gradient-to-br from-primary/15 via-background to-background">
        <div className="container-x py-16 md:py-20 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="eyebrow mb-3">İletişim</div>
            <h2 className="heading-lg">Bir adım uzaktayız.</h2>
            <p className="mt-4 text-muted-foreground max-w-md">
              Soruların, kayıt taleplerin ve iş birlikleri için doğrudan WhatsApp üzerinden bize ulaşabilirsin.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" className="btn-primary" data-testid="cta-whatsapp">
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
            <a href="tel:+905078664360" className="btn-secondary" data-testid="cta-phone">
              <Phone className="h-4 w-4" /> {PHONE_DISPLAY}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ number, label }) {
  return (
    <div>
      <div className="font-display text-4xl md:text-5xl font-black text-primary leading-none">{number}</div>
      <div className="mt-2 text-[10px] md:text-xs font-display font-bold uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
    </div>
  );
}

function Feature({ icon: Icon, title, text }) {
  return (
    <div className="card-elev p-5">
      <div className="h-10 w-10 rounded-md bg-primary/10 ring-1 ring-primary/20 flex items-center justify-center text-primary mb-3">
        <Icon className="h-5 w-5" />
      </div>
      <h4 className="font-display font-bold uppercase tracking-wider text-base">{title}</h4>
      <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{text}</p>
    </div>
  );
}
